import type { BonusGoal } from "@/hooks/use-bonus-goals";
import { bonusMetricStatus, type StatusTone } from "@/lib/widget-status";

/**
 * Fremoverskuende forklaring for rate-mål. En rate akkumuleres ikke; samme
 * avstemte nivå antas derfor å fortsette gjennom resten av året.
 */
export function rateMetricOutlook({
  achieved,
  progress,
}: {
  achieved: boolean;
  progress: number;
}): string {
  if (achieved) {
    return "Fortsetter vi slik, vil vi være innenfor målet ved årsslutt.";
  }
  if (progress >= 75) {
    return "Vi er nær målet, men må forbedre utviklingen for å være innenfor ved årsslutt.";
  }
  return "Fortsetter vi slik, vil vi ikke være innenfor målet ved årsslutt.";
}

export interface DeviationProjectInput {
  id: string;
  project_status: string | null;
  completed_at: string | null;
}

/**
 * Prosjektene budsjettavvik-målet regnes på: ferdigstilt i `year`, og når
 * avstemming er på, til og med siste avstemte dato.
 */
export function bonusDeviationProjectIds(
  projects: DeviationProjectInput[],
  year: number,
  reconciledThroughIso?: string,
): string[] {
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year + 1}-01-01`;
  return projects
    .filter(
      (p) =>
        p.project_status === "completed" &&
        p.completed_at != null &&
        p.completed_at >= yearStart &&
        p.completed_at < yearEnd &&
        (!reconciledThroughIso || p.completed_at.slice(0, 10) <= reconciledThroughIso),
    )
    .map((p) => p.id);
}

export interface ProjectHours {
  projectId: string;
  soldHours: number;
  spentHours: number;
}

/**
 * Solgte timer (interne budsjettlinjer) og brukte timer (alle førte timer)
 * per prosjekt. Summen er grunnlaget for budsjettavvik-målet (VIB-2990).
 */
export function hoursByProject(
  budgetLines: Array<{ project_id: string; quantity_hours: number | null }>,
  timeEntries: Array<{ project_id: string; duration_seconds: number | null }>,
): ProjectHours[] {
  const map = new Map<string, ProjectHours>();
  const get = (id: string) => {
    let row = map.get(id);
    if (!row) {
      row = { projectId: id, soldHours: 0, spentHours: 0 };
      map.set(id, row);
    }
    return row;
  };
  for (const bl of budgetLines) get(bl.project_id).soldHours += Number(bl.quantity_hours ?? 0);
  for (const te of timeEntries) get(te.project_id).spentHours += Number(te.duration_seconds ?? 0) / 3600;
  return [...map.values()];
}

export interface GoalView {
  reached: boolean;
  tone: StatusTone;
  statusText: string;
  bonusEarned: boolean;
}

/**
 * Status drives av PROGNOSE-oppnåelse (forventet/mål), men bonus utløses kun
 * på faktisk OPPNÅDD — aldri på prognose. For tilstandsmål (faktureringsgrad,
 * avvik) er `achieved` det reelle bonus-kriteriet og `progress` driver "nær mål".
 */
export function bonusGoalView(goal: BonusGoal): GoalView {
  if (goal.metricStatus) {
    const status = bonusMetricStatus({
      kind: goal.kind,
      achieved: goal.achieved,
      progress: goal.progress,
      target: goal.target,
      forecastTotal: goal.forecastTotal,
    });
    return {
      reached: status.tone === 'reached',
      tone: status.tone,
      statusText: status.label,
      bonusEarned: goal.achieved,
    };
  }

  if (goal.kind === 'cumulative') {
    const oppnadd = goal.oppnadd ?? goal.current;
    const prognose = goal.prognose ?? 0;
    const forventet = oppnadd + prognose;
    const attain = goal.target > 0 ? forventet / goal.target : 0;
    const reached = attain >= 1;
    const near = !reached && attain >= 0.75;
    const tone: StatusTone = reached ? 'reached' : near ? 'near' : 'miss';
    return {
      reached,
      tone,
      statusText: reached ? 'I mål' : near ? 'Nær mål' : 'Ikke i mål',
      bonusEarned: goal.achieved,
    };
  }
  const reached = goal.achieved;
  const near = !reached && goal.progress >= 75;
  const tone: StatusTone = reached ? 'reached' : near ? 'near' : 'miss';
  return {
    reached,
    tone,
    statusText: reached ? 'I mål' : near ? 'Nær mål' : 'Ikke i mål',
    bonusEarned: goal.achieved,
  };
}
